import { Box, Button, Chip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useState } from "react";
import { InvoiceItemModel } from "types/generated/models";
import Row from "../../Shared/Row";
import { useAnalytics } from "hooks/useAnalytics";
import InvoiceViewStripePayDialog from "../InvoiceViewStripePayDialog";
import { getServiceValue } from "../../../utils/serviceValue";

interface IInvoiceItemProps {
    invoice: InvoiceItemModel;
}

export default function InvoiceViewItem({ invoice }: IInvoiceItemProps) {
    const [showStripePayDialog, setShowStripePayDialog] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { track } = useAnalytics();

    const downloadInvoice = useCallback(() => {
        if (!invoice.downloadInvoiceUrl) {
            return;
        }

        track("invoice_view_download_clicked", {
            invoice_id: invoice.id,
            invoice_number: invoice.invoiceNumber,
            amount_owing: invoice.amountOwingDecimal,
        });

        globalThis.location.assign(invoice.downloadInvoiceUrl);
    }, [invoice, track]);

    const payInvoice = useCallback(() => {
        track("invoice_view_pay_clicked", {
            invoice_id: invoice.id,
            invoice_number: invoice.invoiceNumber,
            amount_owing: invoice.amountOwingDecimal,
        });
        setShowStripePayDialog(true);
    }, [invoice, track]);

    const handleClose = () => {
        setShowStripePayDialog(false);
    };

    return (
        <Box
            sx={{
                border: "1px solid #D4D4D9",
                borderRadius: "4px",
                padding: "24px",
            }}
            data-test-id={`section_invoice_item_${invoice.invoiceNumber}`}
        >
            <Box sx={{ display: "flex", flexDirection: "row", gap: "8px", marginBottom: "8px" }}>
                <Typography variant="subtitle1" fontWeight="bold" data-test-id="text_invoice_date">
                    {invoice.invoiceDate}
                </Typography>
                <Typography variant="subtitle1" data-test-id="text_invoice_number">
                    {`Invoice #${invoice.invoiceNumber}`}
                    <Chip
                        sx={{ marginLeft: "8px" }}
                        label={invoice.isPaid ? "Paid" : "Unpaid"}
                        color={invoice.isPaid ? "success" : "default"}
                        size="small"
                        data-test-id="status_chip"
                    />
                </Typography>
            </Box>
            <Box
                sx={{
                    flexDirection: isMobile ? "column" : "row",
                    display: "flex",
                    gap: isMobile ? "24px" : "0",
                    overflow: "hidden",
                    minWidth: 0,
                }}
            >
                <Box
                    sx={isMobile ? { width: "100%", minWidth: 0, overflow: "hidden" } : { flex: "1 1 0", minWidth: 0 }}
                >
                    <Row subtitle="Provider" value={invoice.practitionerName} dataTestIdSuffix="practitioner_name" />
                    <Row subtitle="Service" value={getServiceValue(invoice.services)} dataTestIdSuffix="service" />
                    <Row subtitle="Client" value={invoice.clientName} dataTestIdSuffix="client_name" />
                </Box>
                <Box
                    sx={isMobile ? { width: "100%", minWidth: 0, overflow: "hidden" } : { flex: "1 1 0", minWidth: 0 }}
                >
                    <Row subtitle="Invoice Total" value={invoice.invoiceTotal} dataTestIdSuffix="invoice_total" />
                    <Row subtitle="Paid" value={invoice.amountPaid} dataTestIdSuffix="amount_paid" />
                    <Row subtitle="Owing" value={invoice.amountOwing} dataTestIdSuffix="amount_owing" />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                        alignItems: isMobile ? "flex-start" : "flex-end",
                        gap: "8px",
                    }}
                >
                    {!invoice.isPaid && invoice.practitioner?.stripeAccount && (
                        <Button
                            sx={{ width: isMobile ? "100%" : "100px" }}
                            size="extraSmall"
                            variant="contained"
                            onClick={payInvoice}
                            data-test-id="button_pay_invoice"
                        >
                            Pay
                        </Button>
                    )}
                    <Button
                        sx={{ width: isMobile ? "100%" : "100px" }}
                        size="extraSmall"
                        variant="outlined"
                        onClick={downloadInvoice}
                        data-test-id="button_download_invoice"
                    >
                        Download
                    </Button>
                </Box>
            </Box>
            {showStripePayDialog && <InvoiceViewStripePayDialog invoice={invoice} handleClose={handleClose} />}
        </Box>
    );
}
